import type { ReduxError } from '@universal/errors';
import { produce } from 'immer';
import { Action, createAsyncAction, createReducer } from 'typesafe-actions';
import { LocalDepartments, LocalDepartmentItem, LocationSlugs } from '@appTypes/nafLocations';

export enum Actions {
  GET_NAF_LOCATIONS = 'naf/centers/GET_NAF_LOCATIONS',
  GET_NAF_LOCATIONS_SUCCESS = 'naf/centers/GET_NAF_LOCATIONS_SUCCESS',
  GET_NAF_LOCATIONS_FAIL = 'naf/centers/GET_NAF_LOCATIONS_FAIL',
  GET_NAF_LOCATIONS_CANCEL = 'naf/centers/GET_NAF_LOCATIONS_CANCEL',
}

/** Whole list in one request — a few hundred items, filtered client-side. Shared with the SSR prefetch. */
export const NAF_LOCATIONS_TAKE = 1000;

type FetchState = 'initial' | 'isUpdating' | 'success' | 'error' | 'cancelled';

export interface State {
  locations: Record<
    LocationSlugs,
    {
      data?: LocalDepartments[] | LocalDepartmentItem[];
      totalCount?: number | null;
      fetchState: FetchState;
      error?: ReduxError;
      previousTake?: number;
      previousCounties?: string[];
    }
  >;
}

export const initialState: State = {
  locations: {
    [LocationSlugs.lokalavdeling]: {
      fetchState: 'initial',
    },
    [LocationSlugs.mc]: {
      fetchState: 'initial',
    },
    [LocationSlugs.nafCenters]: {
      fetchState: 'initial',
    },
    [LocationSlugs.practiceTracks]: {
      fetchState: 'initial',
    },
  },
};

export const actions = {
  getNafLocations: createAsyncAction(
    Actions.GET_NAF_LOCATIONS, // request payload creator
    Actions.GET_NAF_LOCATIONS_SUCCESS, // success payload creator
    Actions.GET_NAF_LOCATIONS_FAIL, // failure payload creator
  )<
    { search: LocationSlugs; take?: number; counties?: string[]; ignoreCounties?: boolean },
    {
      items: LocalDepartments[] | LocalDepartmentItem[];
      total: number;
      name: LocationSlugs;
      take: number;
      counties?: string[];
    },
    { error: ReduxError; name: LocationSlugs }
  >(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getNafLocations.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.locations[action.payload.search] = {
        ...draftState.locations[action.payload.search],
        fetchState: 'isUpdating',
      };
    }),
  )
  .handleAction(actions.getNafLocations.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.locations[action.payload.name] = {
        data: action.payload.items,
        totalCount: action.payload.total,
        fetchState: 'success',
        error: undefined,
        previousTake: action.payload.take,
        previousCounties: action.payload.counties,
      };
    }),
  )
  .handleAction(actions.getNafLocations.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.locations[action.payload.name] = {
        ...draftState.locations[action.payload.name],
        fetchState: 'error',
        error: action.payload.error,
      };
    }),
  );
